import type { CheckInRewardType } from '@/schema'
import type { DailyCheckInFormValues } from '../components/FormSchema'

export type GeneratorMode = 'FLAT' | 'INCREMENT'

export interface RewardItemConfig {
	rewardType: CheckInRewardType
	// FLAT
	amount?: number
	// INCREMENT
	start?: number
	step?: number
	max?: number
	// Eligibility requirements (flat, same every occurrence)
	minimumTurnoverAmount?: number
	minimumDepositAmount?: number
}

export interface GeneratorConfig {
	days: number
	mode: GeneratorMode
	rewardItems: RewardItemConfig[]
}

export function computeCheckInDays(
	config: GeneratorConfig,
): DailyCheckInFormValues['checkInSetting'] {
	return Array.from({ length: config.days }, (_, i) => {
		const itemIndex = i % config.rewardItems.length
		const rewardItem = config.rewardItems[itemIndex]
		// How many times this particular reward item has appeared so far (0-based)
		const occurrenceIndex = Math.floor(i / config.rewardItems.length)

		let amount: number
		if (config.mode === 'FLAT') {
			amount = rewardItem.amount ?? 0
		} else {
			const start = rewardItem.start ?? 0
			const step = rewardItem.step ?? 0
			const max = rewardItem.max ?? Infinity
			amount = Math.min(start + occurrenceIndex * step, max)
		}

		return {
			day: i + 1,
			rewardType: rewardItem.rewardType,
			amount,
			minimumTurnoverAmount: rewardItem.minimumTurnoverAmount ?? undefined,
			minimumDepositAmount: rewardItem.minimumDepositAmount ?? undefined,
			presetId: undefined,
			gameId: undefined,
			betAmount: undefined,
		}
	})
}

export function isRewardItemConfigValid(
	rewardItem: RewardItemConfig,
	mode: GeneratorMode,
): boolean {
	if (!rewardItem.rewardType) return false
	if (mode === 'FLAT') {
		return rewardItem.amount !== undefined && rewardItem.amount >= 0
	}
	// INCREMENT
	return (
		rewardItem.start !== undefined &&
		rewardItem.start >= 0 &&
		rewardItem.step !== undefined &&
		rewardItem.step >= 0 &&
		rewardItem.max !== undefined &&
		rewardItem.max >= rewardItem.start
	)
}

export function isGeneratorConfigValid(config: GeneratorConfig): boolean {
	if (!config.days || config.days < 1 || config.days > 25) return false
	if (config.rewardItems.length < 1 || config.rewardItems.length > 4)
		return false
	return config.rewardItems.every((item) =>
		isRewardItemConfigValid(item, config.mode),
	)
}
